import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { InstaPassOrb } from "../Components/Home/hailey/InstaPassOrb";
import "../Components/Home/hailey/hailey-hero.css";
import useHaileyVoice from "./useHaileyVoice";

const INTRO =
  "Hey! I'm HAILEY, your InstaPass Intelligence Captain. Got followers? I've got opportunities. I can help you become a Creator Partner, get your personal QR code, discover events to promote, and understand how your earnings work. Ready to turn your influence into ticket sales?";

const PUBLIC_SUGGESTIONS = ["How do I become a creator?", "How much do I earn per ticket?", "What events can I promote?", "Can I promote more than one event?"];
const CREATOR_SUGGESTIONS = ["How many tickets have I sold?", "How much money have I earned?", "Where is my QR code?", "How do I get paid?", "What's pending vs approved?"];

/** One HAILEY line, with the words she has already said lit up. */
function SpokenLine({ text, said }) {
  let k = -1;
  return String(text || "")
    .split(/(\s+)/)
    .map((part, i) => {
      if (!part) return null;
      if (/^\s+$/.test(part)) return part;
      k += 1;
      const cls = k < said ? (k === said - 1 ? "cp-w said now" : "cp-w said") : "cp-w";
      return (
        <span key={i} className={cls}>
          {part}
        </span>
      );
    });
}

/**
 * HAILEY in Creator Partner mode: the InstaPass orb, her voice and word
 * highlighting (same voice service as the homepage), plus mic input where the
 * browser supports it. Answers come from InstaPass data via /api/creator/hailey.
 */
export default function HaileyPanel({ signedIn = false, onAction }) {
  const [messages, setMessages] = useState([{ who: "hailey", text: INTRO }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false); // turns on the first time they tap the orb
  const [listening, setListening] = useState(false);
  const msgsRef = useRef(null);
  const recRef = useRef(null);
  const voice = useHaileyVoice();
  const canListen = typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  useEffect(() => () => recRef.current?.abort?.(), []);

  async function send(text) {
    const q = String(text || "").trim();
    if (!q || busy) return;
    voice.stop();
    const replyIndex = messages.length + 1;
    setMessages((m) => [...m, { who: "user", text: q }]);
    setInput("");
    setBusy(true);
    voice.setOrbState("thinking");
    try {
      const res = await api("/hailey", { method: "POST", body: { message: q } });
      setMessages((m) => [...m, { who: "hailey", text: res.reply }]);
      voice.setOrbState("idle");
      if (voiceOn) voice.speak(res.reply, replyIndex);
      if (res.action && onAction) onAction(res.action);
    } catch {
      setMessages((m) => [...m, { who: "hailey", text: "I couldn't reach InstaPass just now. Try again in a moment." }]);
      voice.setOrbState("idle");
    } finally {
      setBusy(false);
    }
  }

  function tapOrb() {
    voice.unlock();
    if (voice.orbState === "speaking") {
      voice.stop();
      return;
    }
    if (!voiceOn) {
      setVoiceOn(true);
      const last = messages.length - 1;
      voice.speak(messages[last].text, last); // say the latest thing she wrote
      return;
    }
    startMic();
  }

  function startMic() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || listening) return;
    voice.stop();
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      setInput((finalText + interim).trim());
    };
    rec.onend = () => {
      setListening(false);
      voice.setOrbState("idle");
      if (finalText.trim()) send(finalText);
    };
    rec.onerror = () => {
      setListening(false);
      voice.setOrbState("idle");
    };
    recRef.current = rec;
    setListening(true);
    voice.setOrbState("listening");
    try {
      rec.start();
    } catch {
      setListening(false);
      voice.setOrbState("idle");
    }
  }

  function toggleVoice() {
    voice.unlock();
    if (voiceOn) voice.stop();
    setVoiceOn((v) => !v);
  }

  const suggestions = signedIn ? CREATOR_SUGGESTIONS : PUBLIC_SUGGESTIONS;
  const hint = listening
    ? "Listening…"
    : voice.orbState === "speaking"
    ? "Tap to stop"
    : busy
    ? "Thinking…"
    : !voiceOn
    ? "Tap HAILEY to hear her"
    : canListen
    ? "Tap to talk"
    : "Type below to ask";

  return (
    <section className="cp-hailey" aria-label="Chat with HAILEY">
      <div className="cp-hailey-stage">
        <InstaPassOrb state={voice.orbState} amp={voice.amp} onTap={tapOrb} className="cp-hailey-orb" />
        <div className="cp-hailey-id">
          <b>HAILEY</b>
          <span>InstaPass Intelligence Captain</span>
          <em aria-live="polite">{hint}</em>
        </div>
        {voice.available && (
          <button
            type="button"
            className="cp-voice-toggle"
            onClick={toggleVoice}
            aria-pressed={voiceOn}
            aria-label={voiceOn ? "Turn HAILEY's voice off" : "Turn HAILEY's voice on"}
          >
            {voiceOn ? "Voice on" : "Voice off"}
          </button>
        )}
      </div>
      <div className="cp-msgs" ref={msgsRef} aria-live="polite">
        {messages.map((m, i) => (
          <div key={i} className={`cp-msg ${m.who}`}>
            {m.who === "hailey" && voice.speech.key === i ? <SpokenLine text={m.text} said={voice.speech.n} /> : m.text}
          </div>
        ))}
        {busy && <div className="cp-msg hailey cp-typing">HAILEY is thinking…</div>}
      </div>
      <div className="cp-suggest">
        {suggestions.map((s) => (
          <button key={s} type="button" onClick={() => send(s)} disabled={busy}>
            {s}
          </button>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          voice.unlock();
          send(input);
        }}
      >
        {canListen && (
          <button
            type="button"
            className={`cp-mic${listening ? " on" : ""}`}
            onClick={() => {
              voice.unlock();
              if (listening) recRef.current?.stop?.();
              else startMic();
            }}
            aria-label={listening ? "Stop listening" : "Speak to HAILEY"}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
            </svg>
          </button>
        )}
        <input className="cp-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask HAILEY" aria-label="Message HAILEY" maxLength={500} />
        <button className="cp-btn small" type="submit" disabled={busy || !input.trim()}>
          Send
        </button>
      </form>
    </section>
  );
}
